import {
  ApiResponse,
  TicketManagerAssignableUser,
  TicketManagerRootCauseReason,
  TicketManagerTicket,
  TicketManagerTicketDetail,
  TicketManagerTicketStatus,
} from '@vision/ui/interfaces';
import { createHttpExtraOptions } from '../../../utils';
import { TicketManagerApi } from '../ticket-manager.api';
import { TicketStatusEndpoints } from '../statuses/ticket-statuses.endpoints';
import { normalizeTicketManagerRootCausesResponse } from '../root-causes/root-causes.mappers';
import {
  TicketManagerTicketAssignableUsersArgs,
  TicketManagerTicketAssignArgs,
  TicketManagerTicketChangeOwnerArgs,
  TicketManagerTicketChangePoolArgs,
  TicketManagerTicketChangePriorityArgs,
  TicketManagerTicketChangeRootCauseGroupArgs,
  TicketManagerTicketChangeStatusArgs,
  TicketManagerTicketDetailArgs,
  TicketManagerTicketMutationSuccessResponse,
  TicketManagerTicketRootCauseArgs,
  TicketManagerTicketRootCausesArgs,
} from './ticket-detail.api.types';
import {
  normalizeTicketManagerAssignableUsersResponse,
  normalizeTicketManagerTicketDetailResponse,
  type TicketManagerAssignableUserResponseEnvelope,
  type TicketManagerTicketDetailResponseEnvelope,
} from './ticket-detail.mappers';
import {
  buildGetTicketRootCausesRequest,
  buildTicketAssignableUsersRequest,
  buildTicketManagerTicketAddRootCauseRequest,
  buildTicketManagerTicketAssignRequest,
  buildTicketManagerTicketChangeOwnerRequest,
  buildTicketManagerTicketChangePoolRequest,
  buildTicketManagerTicketChangePriorityRequest,
  buildTicketManagerTicketChangeRootCauseGroupRequest,
  buildTicketManagerTicketChangeStatusRequest,
  buildTicketManagerTicketDetailRequest,
  buildTicketManagerTicketRemoveRootCauseRequest,
} from './ticket-detail.routes';
import { TicketListEndpoints } from './ticket-lists.endpoints';

const mutationExtraOptions = createHttpExtraOptions({
  notification: {
    autoHandleSuccess: false,
  },
});

function buildTicketManagerStatusFromKey(status: string): TicketManagerTicketStatus {
  return {
    category: status,
    id: status,
    key: status,
    name: status,
    system: false,
  };
}

function updateTicketStatusInCollection(
  tickets: TicketManagerTicket[],
  ticketId: string,
  status: TicketManagerTicketStatus,
) {
  const ticket = tickets.find((ticketItem) => ticketItem.id === ticketId);

  if (ticket) {
    ticket.status = status;
  }
}

function updateTicketDetailRootCauseNames(ticket: TicketManagerTicketDetail) {
  ticket.root_cause_names =
    ticket.root_causes
      .map((rootCause) => rootCause.name)
      .filter(Boolean)
      .join(', ') || '-';
}

export const TicketDetailEndpoints = TicketManagerApi.injectEndpoints({
  endpoints: (builder) => ({
    ticketManagerTicketDetail: builder.query<ApiResponse<TicketManagerTicketDetail>, TicketManagerTicketDetailArgs>({
      query: ({ projectId, ticketId }) => buildTicketManagerTicketDetailRequest({ projectId, ticketId }),
      transformResponse: (response: TicketManagerTicketDetailResponseEnvelope) =>
        normalizeTicketManagerTicketDetailResponse(response),
      providesTags: (_result, _error, { ticketId }) => [{ type: 'TicketManagerTicketDetail', id: ticketId }],
    }),
    ticketManagerTicketAssignableUsers: builder.query<
      ApiResponse<TicketManagerAssignableUser[]>,
      TicketManagerTicketAssignableUsersArgs
    >({
      query: ({ projectId, ticketId }) => buildTicketAssignableUsersRequest({ projectId, ticketId }),
      transformResponse: (response: TicketManagerAssignableUserResponseEnvelope) =>
        normalizeTicketManagerAssignableUsersResponse(response),
      providesTags: (_result, _error, { ticketId }) => [{ type: 'TicketManagerTicketAssignableUsers', id: ticketId }],
    }),
    ticketManagerTicketRootCauses: builder.query<
      ApiResponse<TicketManagerRootCauseReason[]>,
      TicketManagerTicketRootCausesArgs
    >({
      query: ({ projectId, ticketId }) => buildGetTicketRootCausesRequest({ projectId, ticketId }),
      transformResponse: normalizeTicketManagerRootCausesResponse,
      providesTags: (_result, _error, { ticketId }) => [{ type: 'TicketManagerTicketRootCauses', id: ticketId }],
    }),
    changeTicketManagerTicketPriority: builder.mutation<
      TicketManagerTicketMutationSuccessResponse,
      TicketManagerTicketChangePriorityArgs
    >({
      query: ({ priority, projectId, ticketId }) => ({
        ...buildTicketManagerTicketChangePriorityRequest({ priority, projectId, ticketId }),
        extraOptions: mutationExtraOptions,
      }),
      onQueryStarted: async ({ priority, projectId, ticketId }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          TicketDetailEndpoints.util.updateQueryData('ticketManagerTicketDetail', { projectId, ticketId }, (draft) => {
            draft.data.priority = priority;
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    changeTicketManagerTicketOwner: builder.mutation<
      TicketManagerTicketMutationSuccessResponse,
      TicketManagerTicketChangeOwnerArgs
    >({
      query: ({ ownerId, ownerType, projectId, ticketId }) => ({
        ...buildTicketManagerTicketChangeOwnerRequest({ ownerId, ownerType, projectId, ticketId }),
        extraOptions: mutationExtraOptions,
      }),
      onQueryStarted: async ({ ownerId, ownerType, projectId, ticketId }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          TicketDetailEndpoints.util.updateQueryData('ticketManagerTicketDetail', { projectId, ticketId }, (draft) => {
            draft.data.owner_id = ownerId;
            draft.data.owner_type = ownerType;
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    changeTicketManagerTicketPool: builder.mutation<
      TicketManagerTicketMutationSuccessResponse,
      TicketManagerTicketChangePoolArgs
    >({
      query: ({ projectId, ticketId, ticketPoolId }) => ({
        ...buildTicketManagerTicketChangePoolRequest({ projectId, ticketId, ticketPoolId }),
        extraOptions: mutationExtraOptions,
      }),
      onQueryStarted: async ({ projectId, ticketId, ticketPoolId }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          TicketDetailEndpoints.util.updateQueryData('ticketManagerTicketDetail', { projectId, ticketId }, (draft) => {
            draft.data.ticket_pool_id = ticketPoolId;
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    assignTicketManagerTicket: builder.mutation<
      TicketManagerTicketMutationSuccessResponse,
      TicketManagerTicketAssignArgs
    >({
      query: ({ projectId, ticketId, userId }) => ({
        ...buildTicketManagerTicketAssignRequest({ projectId, ticketId, userId }),
        extraOptions: mutationExtraOptions,
      }),
      onQueryStarted: async ({ projectId, ticketId, userId }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          TicketDetailEndpoints.util.updateQueryData('ticketManagerTicketDetail', { projectId, ticketId }, (draft) => {
            draft.data.assignee_id = userId;
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    changeTicketManagerTicketStatus: builder.mutation<
      TicketManagerTicketMutationSuccessResponse,
      TicketManagerTicketChangeStatusArgs
    >({
      query: ({ projectId, status, ticketId }) => ({
        ...buildTicketManagerTicketChangeStatusRequest({ projectId, status, ticketId }),
        extraOptions: mutationExtraOptions,
      }),
      onQueryStarted: async ({ projectId, status, ticketId }, { dispatch, getState, queryFulfilled }) => {
        const cachedStatus =
          TicketStatusEndpoints.endpoints.ticketManagerTicketAvailableStatuses
            .select({ projectId, ticketId })(getState())
            .data?.data.find((statusOption) => statusOption.key === status) || buildTicketManagerStatusFromKey(status);

        const patchResults = [
          dispatch(
            TicketDetailEndpoints.util.updateQueryData(
              'ticketManagerTicketDetail',
              { projectId, ticketId },
              (draft) => {
                draft.data.status = cachedStatus;
              },
            ),
          ),
        ];

        TicketListEndpoints.util
          .selectCachedArgsForQuery(getState(), 'ticketManagerPoolTickets')
          .filter((args) => args.projectId === projectId)
          .forEach((args) => {
            patchResults.push(
              dispatch(
                TicketListEndpoints.util.updateQueryData('ticketManagerPoolTickets', args, (draft) => {
                  updateTicketStatusInCollection(draft.data, ticketId, cachedStatus);
                }),
              ),
            );
          });

        TicketListEndpoints.util
          .selectCachedArgsForQuery(getState(), 'ticketManagerQueueTickets')
          .filter((args) => args.projectId === projectId)
          .forEach((args) => {
            patchResults.push(
              dispatch(
                TicketListEndpoints.util.updateQueryData('ticketManagerQueueTickets', args, (draft) => {
                  updateTicketStatusInCollection(draft.data, ticketId, cachedStatus);
                }),
              ),
            );
          });

        try {
          await queryFulfilled;
        } catch {
          patchResults.reverse().forEach((patchResult) => patchResult.undo());
        }
      },
    }),
    changeTicketManagerTicketRootCauseGroup: builder.mutation<
      TicketManagerTicketMutationSuccessResponse,
      TicketManagerTicketChangeRootCauseGroupArgs
    >({
      query: ({ projectId, rootCauseGroupId, ticketId }) => ({
        ...buildTicketManagerTicketChangeRootCauseGroupRequest({ projectId, rootCauseGroupId, ticketId }),
        extraOptions: mutationExtraOptions,
      }),
      invalidatesTags: (_result, error, { ticketId }) =>
        error ? [] : [{ type: 'TicketManagerTicketRootCauses', id: ticketId }],
      onQueryStarted: async ({ projectId, rootCauseGroupId, ticketId }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          TicketDetailEndpoints.util.updateQueryData('ticketManagerTicketDetail', { projectId, ticketId }, (draft) => {
            draft.data.rootCauseGroupId = rootCauseGroupId;
            draft.data.root_causes = [];
            draft.data.root_cause_names = '-';
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    addTicketManagerTicketRootCause: builder.mutation<
      TicketManagerTicketMutationSuccessResponse,
      TicketManagerTicketRootCauseArgs
    >({
      query: ({ projectId, rootCauseId, ticketId }) => ({
        ...buildTicketManagerTicketAddRootCauseRequest({ projectId, rootCauseId, ticketId }),
        extraOptions: mutationExtraOptions,
      }),
      onQueryStarted: async ({ projectId, rootCauseId, ticketId }, { dispatch, getState, queryFulfilled }) => {
        const rootCauseOption = TicketDetailEndpoints.endpoints.ticketManagerTicketRootCauses
          .select({ projectId, ticketId })(getState())
          .data?.data.find((rootCause) => rootCause.id === rootCauseId);

        const patchResult = dispatch(
          TicketDetailEndpoints.util.updateQueryData('ticketManagerTicketDetail', { projectId, ticketId }, (draft) => {
            if (draft.data.root_causes.some((rootCause) => rootCause.id === rootCauseId)) {
              return;
            }

            draft.data.root_causes.push({
              id: rootCauseId,
              name: rootCauseOption?.name || rootCauseId,
            });
            updateTicketDetailRootCauseNames(draft.data);
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    removeTicketManagerTicketRootCause: builder.mutation<
      TicketManagerTicketMutationSuccessResponse,
      TicketManagerTicketRootCauseArgs
    >({
      query: ({ projectId, rootCauseId, ticketId }) => ({
        ...buildTicketManagerTicketRemoveRootCauseRequest({ projectId, rootCauseId, ticketId }),
        extraOptions: mutationExtraOptions,
      }),
      onQueryStarted: async ({ projectId, rootCauseId, ticketId }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          TicketDetailEndpoints.util.updateQueryData('ticketManagerTicketDetail', { projectId, ticketId }, (draft) => {
            draft.data.root_causes = draft.data.root_causes.filter((rootCause) => rootCause.id !== rootCauseId);
            updateTicketDetailRootCauseNames(draft.data);
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});

export const {
  useAddTicketManagerTicketRootCauseMutation,
  useAssignTicketManagerTicketMutation,
  useChangeTicketManagerTicketOwnerMutation,
  useChangeTicketManagerTicketPoolMutation,
  useChangeTicketManagerTicketPriorityMutation,
  useChangeTicketManagerTicketRootCauseGroupMutation,
  useChangeTicketManagerTicketStatusMutation,
  useRemoveTicketManagerTicketRootCauseMutation,
  useTicketManagerTicketAssignableUsersQuery,
  useTicketManagerTicketDetailQuery,
  useTicketManagerTicketRootCausesQuery,
} = TicketDetailEndpoints;
